import { useMemo, useState } from 'react';
import { Filter, Search, Users, Wallet } from 'lucide-react';
import { BosDurum, Girdi, Rozet, VeriTablo, type ColumnDef } from '@emanetx/ui';
import type { Musteri } from '@emanetx/sdk';
import { useMusteriListesi } from './api.js';

const PARA_SEMBOL: Record<string, string> = { TRY: '₺', EUR: '€', USD: '$' };

function telefonMaskele(t: string): string {
  if (t.length < 10) return t;
  const son2 = t.slice(-2);
  const bas = t.slice(0, t.length - 7);
  return `${bas} *** ** ${son2}`;
}

export function SayfaMusteriler() {
  const [sayfa, setSayfa] = useState(1);
  const [arama, setArama] = useState('');
  const [aktif, setAktif] = useState<'' | 'evet' | 'hayir'>('');

  const sorgu = {
    sayfa,
    boyut: 20,
    ...(arama && { arama }),
    ...(aktif === 'evet' && { aktif: true }),
    ...(aktif === 'hayir' && { aktif: false }),
  };

  const liste = useMusteriListesi(sorgu);

  const kolonlar = useMemo<ColumnDef<Musteri, unknown>[]>(
    () => [
      {
        accessorKey: 'adSoyad',
        header: 'Ad Soyad',
        meta: { etiket: 'Ad Soyad' },
        cell: ({ row }) => (
          <div>
            <div className="font-medium">{row.original.adSoyad}</div>
            {row.original.eposta && (
              <div className="text-fume-500 text-xs">{row.original.eposta}</div>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'telefon',
        header: 'Telefon',
        meta: { etiket: 'Telefon' },
        cell: ({ row }) => (
          <span className="font-mono text-xs">{telefonMaskele(row.original.telefon)}</span>
        ),
      },
      {
        accessorKey: 'cuzdanBakiyesi',
        header: 'Cüzdan',
        meta: { etiket: 'Cüzdan' },
        cell: ({ row }) => {
          const sembol = PARA_SEMBOL[row.original.cuzdanParaBirimi] ?? '';
          const tutar = Number(row.original.cuzdanBakiyesi);
          return (
            <span
              className={`inline-flex items-center gap-1 font-mono text-xs ${tutar > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-fume-500'}`}
            >
              <Wallet size={12} />
              {sembol}
              {row.original.cuzdanBakiyesi}
            </span>
          );
        },
        sortingFn: (a, b) => Number(a.original.cuzdanBakiyesi) - Number(b.original.cuzdanBakiyesi),
      },
      {
        accessorKey: 'toplamKiralama',
        header: 'Kiralama',
        meta: { etiket: 'Kiralama' },
        cell: ({ row }) => (
          <span className="font-mono text-xs">{row.original.toplamKiralama ?? 0}</span>
        ),
      },
      {
        accessorKey: 'aktif',
        header: 'Durum',
        meta: { etiket: 'Durum' },
        cell: ({ row }) =>
          row.original.aktif ? (
            <Rozet ton="yesil" nokta>
              Aktif
            </Rozet>
          ) : (
            <Rozet ton="gri" nokta>
              Pasif
            </Rozet>
          ),
      },
      {
        accessorKey: 'kayitTarihi',
        header: 'Kayıt',
        meta: { etiket: 'Kayıt' },
        cell: ({ row }) => (
          <span className="text-fume-500 font-mono text-xs">
            {new Date(row.original.kayitTarihi).toLocaleDateString('tr-TR', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            })}
          </span>
        ),
      },
      {
        accessorKey: 'sonGirisTarihi',
        header: 'Son giriş',
        meta: { etiket: 'Son giriş' },
        cell: ({ row }) => (
          <span className="text-fume-500 font-mono text-xs">
            {row.original.sonGirisTarihi
              ? new Date(row.original.sonGirisTarihi).toLocaleString('tr-TR', {
                  day: 'numeric',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : '—'}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Müşteriler</h1>
        <p className="text-fume-500 dark:text-fume-400 mt-1 text-sm">
          Kayıtlı mobil uygulama kullanıcıları.
        </p>
      </header>

      <VeriTablo
        veri={liste.data?.veriler ?? []}
        kolonlar={kolonlar}
        toplam={liste.data?.toplam}
        sayfa={sayfa}
        boyut={20}
        onSayfa={setSayfa}
        yukleniyor={liste.isLoading}
        yenileniyor={liste.isFetching}
        onYenile={() => void liste.refetch()}
        varsayilanGizliKolonlar={{ sonGirisTarihi: false }}
        bosDurum={
          <BosDurum
            ikon={Users}
            baslik={arama || aktif ? 'Filtreyle eşleşen müşteri yok' : 'Henüz müşteri yok'}
            aciklama={
              arama || aktif
                ? 'Filtreyi temizleyip tekrar deneyin.'
                : 'Mobil uygulamadan ilk kullanıcı kayıt olduğunda burada görünecek.'
            }
          />
        }
        ustAksiyonlar={
          <>
            <div className="relative min-w-0 flex-1">
              <Search
                size={14}
                className="text-fume-400 pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
              />
              <Girdi
                placeholder="Ad, telefon veya e-posta ile ara..."
                value={arama}
                onChange={(e) => {
                  setArama(e.target.value);
                  setSayfa(1);
                }}
                className="h-10 pl-9 text-xs"
              />
            </div>
            <Filter size={13} className="text-fume-400" />
            <select
              value={aktif}
              onChange={(e) => {
                setAktif(e.target.value as '' | 'evet' | 'hayir');
                setSayfa(1);
              }}
              className="border-fume-200 focus:ring-marka-500 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-50 h-10 rounded-lg border bg-white px-3 text-xs focus:outline-none focus:ring-2"
            >
              <option value="">Tüm müşteriler</option>
              <option value="evet">Sadece aktif</option>
              <option value="hayir">Sadece pasif</option>
            </select>
          </>
        }
      />
    </div>
  );
}
